import { useEffect, useRef, useCallback } from 'react';
import { useAppSelector } from '../../store/hooks';
import { selectPendingOnlineOrderCount } from '../../store/selectors';
import { fetchBusinessById } from '../../services/api';
import { playOnlineOrderSound } from '../../utils/onlineOrderSound';

const SOUND_INTERVAL = 5_000; // 5 seconds

/** Plays a repeating notification sound while there are pending online orders or reservations. */
export default function OnlineOrderSoundWatchdog() {
  const pendingOrders = useAppSelector(selectPendingOnlineOrderCount);
  const pendingReservations = useAppSelector((s) => s.ui.pendingReservationCount);
  const pendingCount = pendingOrders + pendingReservations;
  const soundEnabledRef = useRef(true);
  const soundTypeRef = useRef('chime');
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Fetch sound settings on mount
  useEffect(() => {
    fetchBusinessById(1)
      .then((res) => {
        soundEnabledRef.current = res.data.cfg_online_order_sound !== false;
        soundTypeRef.current = res.data.cfg_online_order_sound_type || 'chime';
      })
      .catch(() => {});
  }, []);

  const clearTimer = useCallback(() => {
    if (intervalRef.current != null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (pendingCount > 0 && soundEnabledRef.current) {
      playOnlineOrderSound(soundTypeRef.current);
      clearTimer();
      intervalRef.current = setInterval(() => {
        if (soundEnabledRef.current) {
          playOnlineOrderSound(soundTypeRef.current);
        }
      }, SOUND_INTERVAL);
    } else {
      clearTimer();
    }
    return clearTimer;
  }, [pendingCount, clearTimer]);

  return null;
}
